export default {
  common: {
    loading: 'Loading…',
    save: 'Save',
    cancel: 'Cancel',
    create: 'Create',
    delete: 'Delete',
    revoke: 'Revoke',
    update: 'Update',
    edit: 'Edit',
    confirm: 'Confirm',
    back: 'Back',
    search: 'Search',
    refresh: 'Refresh',
    yes: 'Yes',
    no: 'No',
    none: 'None',
    all: 'All',
    none_dash: '—',
    error: 'Error',
    success: 'Success',
    pending: 'Pending',
    active: 'Active',
    revoked: 'Revoked',
    disabled: 'Disabled',
    suspended: 'Suspended',
    copied: 'Copied',
    copy: 'Copy',
    copy_failed: 'Copy failed',
    saving: 'Saving…',
    close: 'Close',
    download: 'Download',
    export_csv: 'Export CSV',
    sign_out: 'Sign out',
    language: 'Language',
    actions: 'Actions',
    next: 'Next',
    previous: 'Previous',
    page: 'Page',
    yes_continue: 'Yes, continue',
    saved: 'Saved',
    deleted: 'Deleted',
    failed: 'Failed',
    retry: 'Retry',
    enable: 'Enable',
    disable: 'Disable',
    activate: 'Activate',
    deactivate: 'Deactivate',
    optional: 'Optional',
    required: 'Required',
    apply: 'Apply',
    reset: 'Reset',
    expand: 'Expand',
    collapse: 'Collapse',
    show: 'Show',
    hide: 'Hide',
    created: 'Created',
    updated: 'Updated',
    not_set: 'Not set',
    not_available: 'Not available',
    open_in_new: 'Open in new tab',
  },
  nav: {
    dashboard: 'Dashboard',
    payments: 'Payments',
    webhooks: 'Webhooks',
    settlements: 'Settlements',
    merchants: 'Merchants',
    gateways: 'Gateways',
    audit: 'Audit log',
    users: 'Users',
    security: 'Security',
    help_group: 'Help',
    demo_requests: 'Demo requests',
    user_guide: 'User guide',
    api_reference: 'API reference',
    psp_integrations: 'PSP integration guide',
    webhook_signing: 'Webhook signing',
    event_catalog: 'Event catalog',
  },
  auth: {
    sign_in_title: 'Sign in to PayHub',
    sign_in_subtitle: 'Operator console',
    username: 'Username',
    password: 'Password',
    sign_in: 'Sign in',
    mfa_title: 'Enter your authenticator code',
    mfa_code: 'One-time code',
    mfa_verify: 'Verify',
    mfa_help: 'Open your authenticator app and enter the 6-digit code.',
    invalid_credentials: 'Invalid username or password',
    invalid_code: 'Invalid code',
    demo_banner_title: 'Demo environment',
    demo_use_credentials: 'Use demo credentials',
    demo_request_title: 'Try the PayHub demo',
    demo_request_subtitle:
      'Demo access is by request. Tell us a little about yourself and an operator will email you a one-time login link.',
    demo_request_cta: 'Request access',
    demo_have_token_cta: 'I have a token',
  },
  dashboard: {
    title: 'Dashboard',
    subtitle: 'Last {{hours}} hours',
    inflight: 'Payments in flight',
    payments_by_status: 'Payments by status',
    payments_by_psp: 'Payments by PSP',
    deliveries_by_status: 'Webhook deliveries',
    no_data: 'No data in this window yet.',
    initiated: 'Initiated',
    succeeded: 'Succeeded',
    failed: 'Failed',
    in_flight: 'In flight',
    count: 'Count',
  },
  payments: {
    title: 'Payments',
    subtitle: 'All initiations across merchants and PSPs.',
    filter_psp: 'PSP',
    filter_status: 'Status',
    filter_merchant: 'Merchant',
    no_results: 'No payments match the current filters.',
    cols: {
      created: 'Created',
      psp: 'PSP',
      order_ref: 'Order ref',
      psp_ref: 'PSP ref',
      amount: 'Amount',
      currency: 'Currency',
      status: 'Status',
    },
    detail: {
      title: 'Payment {{id}}',
      back_to_list: 'Back to payments',
      timeline: 'Timeline',
      psp_payload: 'PSP payload',
      metadata: 'Metadata',
      events: 'Events',
      not_found: 'Payment not found.',
      no_events: 'No events recorded yet.',
      event: 'Event',
      source: 'Source',
      from_status: 'From status',
      to_status: 'To status',
      copy_id: 'Copy id',
      checkout_link: 'Hosted checkout link',
      issue_link: 'Issue checkout link',
      link_issued: 'Link issued — copy it now (expires in {{seconds}}s)',
      link_failed: 'Failed to issue checkout link',
      record_refund: 'Record refund',
      refund_dialog_title: 'Record a refund',
      refund_dialog_desc:
        'Libyan PSPs settle refunds in their back-office. Use this form to record that a refund happened so reconciliation balances out and the merchant gets a webhook.',
      refund_amount: 'Amount (minor units)',
      refund_reason: 'Reason (optional)',
      refund_psp_ref: 'PSP back-office reference (optional)',
      refund_full: 'Full refund (transitions payment to refunded)',
      refund_partial: 'Partial refund (logs the event; payment stays succeeded)',
      refund_success: 'Refund recorded; webhook fan-out enqueued.',
      refund_failed: 'Failed to record refund',
      refund_only_succeeded: 'Refunds may only be recorded against succeeded payments.',
      // Re-poll PSP — calls the adapter's query() and applies whatever
      // the PSP says through the standard transition() chokepoint.
      repoll: 'Re-poll PSP',
      repoll_busy: 'Polling…',
      repoll_no_change: 'PSP still inconclusive — payment unchanged.',
      repoll_transitioned: 'PSP confirmed status: {{status}}.',
      repoll_failed: 'Re-poll failed',
      // Force resolve — operator-attested manual transition with reason.
      force_resolve: 'Force resolve',
      force_resolve_title: 'Force-resolve payment',
      force_resolve_desc:
        'Manually transition this payment to a terminal state. Use only when the PSP can no longer give a definitive answer (lost callback, abandoned checkout). The reason is written to the audit log.',
      force_resolve_target_label: 'New status',
      force_resolve_target_succeeded: 'Succeeded — money definitely settled',
      force_resolve_target_failed: 'Failed — payment definitely did not settle',
      force_resolve_target_expired: 'Expired — customer abandoned / past deadline',
      force_resolve_reason_label: 'Reason (required, kept for audit)',
      force_resolve_reason_placeholder:
        'e.g. Mobicash support confirmed the wallet authorisation never reached us — ticket #1234',
      force_resolve_reason_required: 'A reason of at least 4 characters is required.',
      force_resolve_confirm: 'Force-resolve',
      force_resolve_success: 'Payment force-resolved; audit row written.',
      force_resolve_failed: 'Failed to force-resolve payment',
    },
  },
  merchants: {
    title: 'Merchants',
    subtitle: 'Internal sub-tenants on this PayHub install.',
    create: 'New merchant',
    edit: 'Edit merchant',
    delete: 'Delete merchant',
    code: 'Code',
    name: 'Name',
    status: 'Status',
    payments_count: 'Payments',
    create_description: 'Code is the stable identifier in API logs and webhook URLs. Pick carefully.',
    edit_description: 'Update the merchant code, name or status.',
    delete_title: 'Delete merchant',
    delete_confirm: 'Delete this merchant? This cascades API keys, gateways, webhooks, and settlements.',
    delete_blocked: 'Cannot delete: merchant has {{count}} payment(s). Suspend instead.',
    delete_blocked_short: '{{count}} payment(s) exist',
    suspend: 'Suspend',
    unsuspend: 'Resume',
    none: 'No merchants yet.',
    api_keys_tab: 'API keys',
    gateways_tab: 'Gateways',
    webhooks_tab: 'Webhooks',
    detail_back: 'Back to merchants',
    code_invalid: 'Lowercase letters, digits, _ and - only',
    pick_merchant: 'Pick a merchant to manage gateways for.',
  },
  api_keys: {
    title: 'API keys',
    description: 'Keys grant access to /v1/* for this merchant. Each key carries an optional IP allowlist and a rate-limit tier.',
    new: 'New key',
    cols: {
      key_id: 'Key id',
      scopes: 'Scopes',
      tier: 'Tier',
      allowlist: 'IP allowlist',
      status: 'Status',
      last_used: 'Last used',
    },
    none: 'No API keys yet.',
    revoke_confirm: 'Revoke this API key? Customers using it will start failing immediately.',
    revoked_toast: 'Key revoked',
    create_failed: 'Create failed',
    update_failed: 'Update failed',
    revoke_failed: 'Revoke failed',
    edit_title: 'Edit API key',
    edit_description: 'Update the IP allowlist or rate-limit tier. The secret is not re-issued.',
    create_title: 'New API key',
    create_description: 'Scopes are granted to all callers of this key. The IP allowlist is enforced after secret verification; leave it empty for no IP restriction.',
    scopes_label: 'Scopes (one per line)',
    allowlist_label: 'IP allowlist (one CIDR per line, blank = any IP)',
    tier_label: 'Rate-limit tier',
    invalid_cidr: 'Invalid CIDR/IP entries: {{entries}}',
    secret_revealed_title: 'API key created',
    secret_revealed_description: 'Copy this secret now — it will not be shown again. We store only an argon2id hash.',
    secret_revealed_ack: 'I have saved it',
    any_ip: '— any IP',
    updated_toast: 'Key updated',
    tiers: {
      free: 'Free',
      standard: 'Standard',
      premium: 'Premium',
      enterprise: 'Enterprise',
    },
  },
  gateways_panel: {
    title: 'Gateways',
    description: 'Per-PSP credential configuration. Secrets are encrypted at rest with envelope encryption (AES-GCM + KEK).',
    psp_label: 'PSP',
    env_label: 'Environment',
    base_url_label: 'Base URL (override)',
    proxy_label: 'Proxy URL',
    ca_bundle_label: 'CA bundle (PEM)',
    payload_label: 'Credentials',
    active_label: 'Active',
    inactive_label: 'Inactive',
    test: 'Test',
    save: 'Save',
    test_passed: 'Test passed',
    test_failed: 'Test failed',
    saved_toast: 'Gateway saved',
    deactivated_toast: 'Gateway deactivated',
    deactivate_confirm: 'Deactivate this gateway?',
    none: 'No gateways configured yet.',
    secret_placeholder: '••••••• (set; cannot be revealed)',
    add_for: 'Add credentials for {{psp}}',
    no_schema: 'No credential schema for {{psp}}',
  },
  webhook_endpoints: {
    title: 'Webhook endpoints',
    description: 'URLs that receive signed events for this merchant.',
    new: 'New endpoint',
    cols: {
      url: 'URL',
      events: 'Events',
      active: 'Active',
      created: 'Created',
    },
    url_label: 'URL',
    secret_label: 'Secret (≥ 16 chars)',
    events_label: 'Events (comma-separated, * = all)',
    none: 'No endpoints yet.',
    create_title: 'New webhook endpoint',
    deactivate_confirm: 'Deactivate this endpoint?',
    deactivated_toast: 'Endpoint deactivated',
    saved_toast: 'Endpoint created',
  },
  webhook_deliveries: {
    title: 'Webhook deliveries',
    subtitle: 'Outbound deliveries; failed ones can be replayed or marked resolved.',
    cols: {
      next_attempt: 'Next attempt',
      attempt: 'Attempt',
      status: 'Status',
      response: 'Response',
      created: 'Created',
    },
    filter_status: 'Status',
    none: 'No deliveries match.',
    replay: 'Replay',
    resolve: 'Resolve',
    replayed_toast: 'Replay enqueued',
    resolved_toast: 'Marked resolved',
    replay_failed: 'Replay failed',
    resolve_failed: 'Resolve failed',
  },
  settlements: {
    title: 'Settlements',
    subtitle: 'Daily reconciliation files matched against payments.',
    upload: 'Upload settlement',
    pull: 'Pull from PSP',
    cols: {
      uploaded: 'Uploaded',
      psp: 'PSP',
      file: 'File',
      rows: 'Rows',
      matched: 'Matched',
      mismatch: 'Mismatch',
      missing_in_hub: 'Missing in hub',
      missing_in_psp: 'Missing in PSP',
    },
    upload_dialog: {
      title: 'Upload settlement file',
      description: 'CSV file from the PSP back-office.',
      merchant_label: 'Merchant',
      psp_label: 'PSP',
      period_from: 'Period from',
      period_to: 'Period to',
      file_label: 'CSV file',
      submit: 'Upload',
      uploading: 'Uploading…',
      uploaded_toast: 'Ingested {{rows}} rows ({{matched}} matched)',
      upload_failed: 'Upload failed',
    },
    pull_dialog: {
      title: 'Pull settlement from PSP',
      description: 'Triggers a settlement export pull from the PSP for the chosen window.',
      submit: 'Pull',
      pulled_toast: 'Pull enqueued',
      pull_failed: 'Pull failed',
      no_supported: 'No PSP currently supports programmatic pull.',
    },
    detail: {
      back: 'Back',
      rows: 'Rows',
      filter: 'Filter',
      status: 'Status',
      order_ref: 'Order ref',
      psp_ref: 'PSP ref',
      amount: 'Amount',
      psp_status: 'PSP status',
      diff: 'Diff',
      no_rows: 'No rows in this view.',
      not_found: 'Settlement file not found.',
    },
    none: 'No settlements yet.',
  },
  audit: {
    title: 'Audit log',
    subtitle: 'Every administrative mutation is recorded with before/after snapshots.',
    filter_action: 'Action prefix',
    filter_target_type: 'Target type',
    filter_target_id: 'Target id',
    cols: {
      time: 'Time',
      actor: 'Actor',
      action: 'Action',
      target: 'Target',
    },
    no_results: 'No audit rows match.',
    before: 'Before',
    after: 'After',
    expand: 'Expand',
    actor_admin: 'admin',
    actor_system: 'system',
    actor_merchant: 'merchant',
  },
  security: {
    title: 'Security',
    subtitle: 'Account-level security settings.',
    mfa_title: 'Multi-factor authentication',
    mfa_enabled: 'MFA is enabled on your account.',
    mfa_disabled: 'MFA is not yet enabled. Set it up below.',
    enrol: 'Set up MFA',
    confirm: 'Confirm code',
    disable: 'Disable MFA',
    enrol_step: 'Step {{n}} of 2',
    scan_qr: 'Scan this code with your authenticator app, then enter the 6-digit code it generates.',
    secret_label: 'Secret (manual entry)',
    code: '6-digit code',
    password_to_disable: 'Re-enter your password to disable MFA',
    enabled_toast: 'MFA enabled',
    disabled_toast: 'MFA disabled',
    enrol_failed: 'Failed to start enrolment',
    confirm_failed: 'Code did not verify',
    disable_failed: 'Failed to disable MFA',
    issuer_label: 'Issuer',
    account_label: 'Account',
  },
  webhook_verification: {
    title: 'Webhook verification',
    description: 'PayHub signs every outbound webhook so you can prove it came from us and was not tampered with.',
    algorithm_title: 'Algorithm',
    snippets_title: 'Reference implementations',
  },
  event_catalog: {
    title: 'Event catalog',
    subtitle:
      'Every event the hub emits, the status transitions they accompany, and whether they fire an outbound webhook.',
    status_change: 'Status change',
    source: 'Source',
    always_emits: 'Always emits',
    on_match: 'On subscription match',
    never_emits: 'Internal only',
  },
  api_docs: {
    title: 'API reference',
    subtitle: 'Live OpenAPI for /v1 — generated from the running server.',
    open_swagger: 'Open Swagger UI',
    download_json: 'Download openapi.json',
    search_placeholder: 'Search by path, summary or operation id…',
    no_match: 'No operations match.',
    parameters: 'Parameters',
    request_body: 'Request body',
    responses: 'Responses',
    description_label: 'Description',
    of_count: '{{shown}} of {{total}}',
    no_description: 'No description.',
  },
  merchant: {
    nav: {
      gateways: 'Gateways',
      integrations: 'Integration guide',
      users: 'Users',
      org: 'Organisation',
      profile: 'Profile',
    },
  },
  psp_guide: {
    eyebrow: 'PSP integration guide',
    title: 'Integrate Sadad, Moamalat, Mobicash, T-Lync & Adfali',
    subtitle:
      'Step-by-step backend recipes for each PSP — what to send, what to expect, and what to handle on your server. The shared section covers auth, idempotency and webhook signing once; the per-PSP tabs cover only what differs.',
  },
  demo: {
    request: {
      title: 'Request demo access',
      subtitle:
        'Fill in your details. An operator reviews each request and emails you back with a one-time, time-limited login link.',
      name_label: 'Full name',
      email_label: 'Email',
      company_label: 'Company (optional)',
      phone_label: 'Phone (optional)',
      use_case_label: 'What do you want to evaluate? (optional)',
      use_case_placeholder:
        'Briefly describe what you plan to test in the demo — which PSP, which flow, anything we should pre-configure for you.',
      submit: 'Submit request',
      error: 'Failed to submit request. Please try again.',
      thanks_title: 'Request received',
      thanks_subtitle: 'You\'ll hear from us soon',
      thanks_body:
        'We received your request and an operator has been notified. When approved, you will receive an email with a one-time login link.',
      have_token_cta: 'I already have a token',
    },
    login: {
      title: 'Demo token sign-in',
      subtitle: 'Paste the token from your approval email, or open the link directly.',
      token_label: 'Access token',
      token_help: 'Single-use; expires once approved by the operator.',
      submit: 'Sign in',
      error: 'Invalid or expired token.',
      no_token_cta: 'Request access',
    },
  },
  demo_admin: {
    eyebrow: 'Demo access requests',
    title: 'Triage demo access',
    subtitle:
      'Review pending requests, approve them with a TTL to mint a single-use token (auto-emailed to the requester), or reject with a reason.',
    tab_pending: 'Pending',
    tab_approved: 'Approved',
    tab_rejected: 'Rejected',
    empty: 'No requests in this state.',
    field_company: 'Company',
    field_phone: 'Phone',
    field_use_case: 'Use case',
    field_rejection_reason: 'Rejection reason',
    status_pending: 'Pending',
    status_approved: 'Approved',
    status_rejected: 'Rejected',
    status_used: 'Token used',
    status_revoked: 'Revoked',
    status_expired: 'Expired',
    approve: 'Approve',
    reject: 'Reject',
    revoke: 'Revoke token',
    approve_title: 'Approve {{name}}',
    approve_subtitle:
      'Issue a single-use access token. The token is emailed to the requester immediately; the plaintext is also displayed once below in case email delivery fails.',
    approve_confirm: 'Issue token',
    approve_toast: 'Approved — token issued',
    ttl_label: 'Validity (hours)',
    ttl_help: '1–168. Defaults to 24 hours.',
    issued_body:
      'Token issued and emailed. The plaintext is shown only once — copy it now if you need a fallback.',
    issued_link_label: 'One-time login link',
    issued_token_label: 'Token',
    issued_expires: 'Expires {{when}}',
    reject_title: 'Reject {{name}}',
    reject_subtitle: 'The requester is notified by email.',
    reason_label: 'Reason (optional)',
    reason_placeholder:
      'Briefly explain why this request is declined. Visible to the requester.',
    reason_help:
      'Kept on the request row for the audit log. Leave blank for a generic rejection.',
    reject_confirm: 'Reject request',
    reject_toast: 'Rejected',
    revoke_toast: 'Token revoked',
    token_issued: 'Issued',
    token_expires: 'Expires',
    token_used: 'Used',
    token_revoked: 'Revoked',
  },
  guide: {
    title: 'Operator guide',
    subtitle: 'Quick tour of the PayHub admin and how the moving parts fit together.',
    quickstart: 'Quick start',
    quickstart_steps: 'Onboard a merchant → configure their PSP credentials → watch payments flow on the dashboard.',
    section_auth: 'Authentication',
    section_merchants: 'Merchants & API keys',
    section_gateways: 'Gateways (PSP credentials)',
    section_payments: 'Payments',
    section_webhooks: 'Webhooks',
    section_settlements: 'Settlements & reconciliation',
    section_audit: 'Audit log',
    section_security: 'Security & compliance',
    open_section: 'Open',
  },
  empty: {
    nothing_to_show: 'Nothing to show',
    something_went_wrong: 'Something went wrong',
  },
};
